import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type StaticAssetsService from '../../../services/static-assets.ts';
import TioLoadingSpinner from '../loading-spinner.gts';
import { t } from 'ember-intl';
import Modal from 'tio-ui/components/modal';
import { on } from '@ember/modifier';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';

interface DocumentAssetSignature {
  Args: {
    assetName: string;
    content?: string;
    onCancel: () => void;
    show: boolean;
    title: string;
    updateDate?: string;
  };
}

interface DigitalAssetDataInterface {
  title: string;
  text: string;
  updateDate: string;
}

export default class DocumentAssetComponent extends Component<DocumentAssetSignature> {
  @service declare staticAssets: StaticAssetsService;

  @tracked data: DigitalAssetDataInterface | null = null;

  get _triggerLoad() {
    if (this.args.show && this.args.assetName) {
      void this.loadDocument.perform();
    }
    return null;
  }

  get title() {
    return this.data?.title ?? this.args.title;
  }

  get content() {
    return this.data?.text ?? this.args.content;
  }

  get updateDate() {
    return this.data?.updateDate ?? this.args.updateDate;
  }

  loadDocument = dropTask(async () => {
    if (this.args.content || this.data) {
      return;
    }

    this.data = await this.staticAssets.getStaticJson(this.args.assetName);
  });

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{@onCancel}}
      class="min-h-64"
      @size="xl"
      @ariaLabel={{t "common.aria_labels.document_asset"}}
    >
      <:header>
        {{@title}}
        {{#if this.updateDate}}
          -
          {{t "common.dialog.document_asset.last_updated" date=this.updateDate}}
        {{/if}}
      </:header>
      <:body>
        {{this._triggerLoad}}
        {{#if this.loadDocument.isRunning}}
          <TioLoadingSpinner class="self-center" />
        {{/if}}

        <div class="raw-html">
          {{! template-lint-disable no-triple-curlies }}
          {{{this.content}}}
        </div>
      </:body>
      <:footer>
        <Button {{on "click" @onCancel}} @intent="primary">
          {{t "common.ok"}}
        </Button>
      </:footer>
    </Modal>
  </template>
}
